/**
 * Responsive Video Handler
 * Manages conditional video loading and playback for hero/banner sections
 * 
 * Features:
 * - Shows/hides appropriate video based on viewport width
 * - Pauses inactive videos to save CPU/battery
 * - Auto-plays videos when visible in viewport (Intersection Observer)
 * - Debounced resize handling
 * - Proper cleanup to prevent memory leaks
 */

if (typeof ResponsiveVideoManager === 'undefined') {
  class ResponsiveVideoManager {
    constructor() {
      this.videoWrappers = [];
      this.resizeTimers = new Map();
      this.observers = new Map();
      this.MOBILE_BREAKPOINT = 750;
      this.DEBOUNCE_DELAY = 250;
      this.INTERSECTION_THRESHOLD = 0.5; // Video must be 50% visible to autoplay
      
      this.init();
    }

  init() {
    // Find all responsive video wrappers
    const wrappers = document.querySelectorAll('.responsive-video-wrapper');
    
    wrappers.forEach(wrapper => {
      const sectionId = wrapper.dataset.sectionId;
      if (!sectionId) {
        console.warn('[ResponsiveVideoManager] Wrapper missing section ID', wrapper);
        return;
      }
      
      const breakpoint = parseInt(wrapper.dataset.mobileBreakpoint) || this.MOBILE_BREAKPOINT;
      const desktopVideo = wrapper.querySelector('.responsive-video--desktop');
      const mobileVideo = wrapper.querySelector('.responsive-video--mobile');
      const singleVideo = wrapper.querySelector('.responsive-video--all');
      
      this.videoWrappers.push({
        element: wrapper,
        sectionId: sectionId,
        breakpoint: breakpoint,
        desktopVideo: desktopVideo,
        mobileVideo: mobileVideo,
        singleVideo: singleVideo
      });
    });

    // Initialize each wrapper
    this.videoWrappers.forEach(wrapper => {
      this.updateVideoVisibility(wrapper);
      this.setupIntersectionObserver(wrapper);
    });

    // Set up resize listener (single listener for all videos)
    this.setupResizeListener();
  }

  updateVideoVisibility(wrapper) {
    const { element, breakpoint, desktopVideo, mobileVideo, singleVideo } = wrapper;
    
    // Handle single video case (no responsive switching)
    if (singleVideo) {
      const videoEl = singleVideo.querySelector('video');
      if (videoEl && !videoEl.dataset.loaded) {
        this.loadVideo(videoEl);
      }
      return;
    }
    
    if (!desktopVideo || !mobileVideo) {
      return; // No responsive switch needed
    }
    
    const isMobile = window.innerWidth < breakpoint;
    const desktopVideoEl = desktopVideo.querySelector('video');
    const mobileVideoEl = mobileVideo.querySelector('video');
    
    if (isMobile) {
      // Show mobile, hide desktop
      desktopVideo.style.display = 'none';
      mobileVideo.style.display = 'block';
      
      // Load mobile video if not already loaded
      if (mobileVideoEl && !mobileVideoEl.dataset.loaded) {
        this.loadVideo(mobileVideoEl);
      }
      
      // Pause desktop video to save resources (only if already loaded, calling
      // .pause() on an unloaded video triggers the browser to start loading it)
      if (desktopVideoEl && desktopVideoEl.dataset.loaded) {
        desktopVideoEl.pause();
      }
      
      // Play mobile video if visible
      if (mobileVideoEl && mobileVideoEl.paused) {
        this.attemptAutoplay(mobileVideoEl);
      }
    } else {
      // Show desktop, hide mobile
      desktopVideo.style.display = 'block';
      mobileVideo.style.display = 'none';
      
      // Load desktop video if not already loaded
      if (desktopVideoEl && !desktopVideoEl.dataset.loaded) {
        this.loadVideo(desktopVideoEl);
      }
      
      // Pause mobile video to save resources (only if already loaded, calling
      // .pause() on an unloaded video triggers the browser to start loading it)
      if (mobileVideoEl && mobileVideoEl.dataset.loaded) {
        mobileVideoEl.pause();
      }
      
      // Play desktop video if visible
      if (desktopVideoEl && desktopVideoEl.paused) {
        this.attemptAutoplay(desktopVideoEl);
      }
    }
  }

  loadVideo(video) {
    if (!video || video.dataset.loaded) return;
    
    // Trigger video load by setting preload to metadata
    video.preload = 'metadata';
    video.load();
    video.dataset.loaded = 'true';
  }

  setupResizeListener() {
    window.addEventListener('resize', () => {
      this.videoWrappers.forEach(wrapper => {
        // Clear existing timer for this wrapper
        if (this.resizeTimers.has(wrapper.sectionId)) {
          clearTimeout(this.resizeTimers.get(wrapper.sectionId));
        }
        
        // Set new debounced timer
        const timer = setTimeout(() => {
          this.updateVideoVisibility(wrapper);
          this.resizeTimers.delete(wrapper.sectionId);
        }, this.DEBOUNCE_DELAY);
        
        this.resizeTimers.set(wrapper.sectionId, timer);
      });
    }, { passive: true }); // Use passive listener for better scroll performance
  }

  setupIntersectionObserver(wrapper) {
    const { desktopVideo, mobileVideo, singleVideo } = wrapper;
    if (!('IntersectionObserver' in window)) return;
    
    // Only observe videos that will actually be visible
    const videosToObserve = [];
    
    if (singleVideo) {
      const video = singleVideo.querySelector('video');
      if (video) videosToObserve.push(video);
    } else {
      if (desktopVideo) {
        const video = desktopVideo.querySelector('video');
        if (video) videosToObserve.push(video);
      }
      if (mobileVideo) {
        const video = mobileVideo.querySelector('video');
        if (video) videosToObserve.push(video);
      }
    }
    
    if (videosToObserve.length === 0) return;
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const video = entry.target;
        
        if (entry.isIntersecting) {
          // Video is visible - play if not already playing and not hidden
          if (video.paused && video.parentElement.style.display !== 'none') {
            this.attemptAutoplay(video);
          }
        } else {
          // Video is not visible - pause to save resources
          video.pause();
        }
      });
    }, { 
      threshold: this.INTERSECTION_THRESHOLD,
      rootMargin: '50px' // Start loading slightly before video comes into view
    });
    
    videosToObserve.forEach(video => observer.observe(video));
    this.observers.set(wrapper.sectionId, observer);
  }

  attemptAutoplay(video) {
    if (!video) return;
    
    const playPromise = video.play();
    
    if (playPromise !== undefined) {
      playPromise.catch(error => {
        // Autoplay was prevented (common on mobile/some browsers)
        // This is expected behavior, not an error
        console.log('Video autoplay prevented:', error.message);
      });
    }
  }

    // Cleanup method for theme editor or SPA scenarios
    destroy() {
      // Clear all resize timers
      this.resizeTimers.forEach(timer => clearTimeout(timer));
      this.resizeTimers.clear();
      
      // Disconnect all intersection observers
      this.observers.forEach(observer => observer.disconnect());
      this.observers.clear();
      
      // Clear wrapper references
      this.videoWrappers = [];
    }
  }

  // Make the class available globally
  window.ResponsiveVideoManager = ResponsiveVideoManager;
}

// Initialize when DOM is ready (only if not already initialized)
if (!window.responsiveVideoManager) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.responsiveVideoManager = new ResponsiveVideoManager();
    });
  } else {
    window.responsiveVideoManager = new ResponsiveVideoManager();
  }
}

// Support for Shopify theme editor
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    // Reinitialize when sections are added/reordered in theme editor
    if (window.responsiveVideoManager) {
      window.responsiveVideoManager.destroy();
    }
    window.responsiveVideoManager = new ResponsiveVideoManager();
  });
  
  document.addEventListener('shopify:section:unload', () => {
    // Clean up when sections are removed
    if (window.responsiveVideoManager) {
      window.responsiveVideoManager.destroy();
    }
  });
}
